import FAsyncQueue from '~/services/core/async-queue';
import {buildPersistableClipsPayload} from '~/utils/video-editor/persistence-utils.js';
import {getFrameFileName, getFrameUploadResponseKey, getFrameExportDedupeKey} from '~/utils/video-editor/frame-utils.js';
import {uploadBlobToSignedUrl, withRetry} from '~/utils/video-editor/upload-utils.js';
import {getClipRange, getFileDurationSeconds} from '~/utils/video-editor/clip-utils.js';

const TITLE_FRAME_UPLOAD_PROCESS_PARALLEL = 3;

export function useVideoEditorStoreSubmission({clips, file, persistEditingSession, stopAutoPersist}) {
  const isSubmittingExport = ref(false);
  let _submitPromise = null;

  async function exportPendingTitleFrames({service = useVideoEditorApi()} = {}) {
    const frameTargets = [];
    const dedupe = new Set();
    const coreLoadingEvent = useEventBus('core-loading-set');
    const loadingIcon = 'i-ri-vidicon-line';
    const getTitleFrameLoadingMessage = ({completedCount = 0, totalCount = 0} = {}) => {
      const progressSuffix = completedCount && totalCount
        ? ` (${completedCount}/${totalCount} captured)`
        : '';

      return `Creating your title frames...${progressSuffix}`;
    };

    coreLoadingEvent.emit({
      isLoading: true,
      icon: loadingIcon,
      message: getTitleFrameLoadingMessage()
    });

    for (const clip of clips.value) {
      for (const [frameType, frame] of Object.entries(clip.frames || {})) {
        const designProjectId = frame?.design_project_id;
        if (!designProjectId) continue;

        const dedupeKey = getFrameExportDedupeKey({
          clipId: clip.id,
          frameType,
          designProjectId
        });

        if (dedupe.has(dedupeKey)) continue;
        dedupe.add(dedupeKey);

        frameTargets.push({
          clip,
          frame,
          clipId: clip.id,
          frameType,
          designProjectId
        });
      }
    }

    if (!frameTargets.length) {
      return;
    }

    try {
      const projectExport = useProjectExport();
      const exportedByProjectId = new Map();
      const uploadItems = [];
      let completedCount = 0;

      for (const target of frameTargets) {
        let exportedSurface = exportedByProjectId.get(target.designProjectId);

        if (!exportedSurface) {
          exportedSurface = await projectExport.exportSurface({
            projectId: target.designProjectId,
            surfaceIndex: 0,
            exportType: VIDEO_EDITOR_TITLE_FRAME_EXPORT_TYPE
          });
          exportedByProjectId.set(target.designProjectId, exportedSurface);
        }

        const fileName = getFrameFileName({frameType: target.frameType});

        uploadItems.push({
          clipId: target.clipId,
          frameType: target.frameType,
          fileName,
          blob: exportedSurface.blob,
          contentType: exportedSurface.blob?.type || 'image/png',
          surfaceIndex: exportedSurface.surfaceIndex,
          surfaceId: exportedSurface.surfaceId
        });

        completedCount += 1;
        coreLoadingEvent.emit({
          isLoading: true,
          icon: loadingIcon,
          message: getTitleFrameLoadingMessage({
            completedCount,
            totalCount: frameTargets.length
          })
        });
      }

      const presignedResult = await withRetry(
        () => service.getTitleFrameUploadUrls({
          fileId: file.value.id,
          items: uploadItems.map(({blob, ...item}) => item)
        }),
        {attempts: 3, delayMs: 400}
      );

      const presignedErrors = presignedResult?.errors || [];
      const presignedByKey = new Map(
        (presignedResult?.items || []).map(item => [
          getFrameUploadResponseKey({
            clipId: item.clipId,
            frameType: item.frameType,
            fileName: item.fileName
          }),
          item
        ])
      );
      const uploadCandidates = uploadItems.filter(item => presignedByKey.has(
        getFrameUploadResponseKey({
          clipId: item.clipId,
          frameType: item.frameType,
          fileName: item.fileName
        })
      ));
      const uploadQueue = new FAsyncQueue({
        processParallel: TITLE_FRAME_UPLOAD_PROCESS_PARALLEL
      });

      coreLoadingEvent.emit({
        isLoading: true,
        icon: 'i-ri-file-upload-line',
        message: 'Uploading title frames...'
      });

      const uploadResults = await Promise.allSettled(
        uploadCandidates.map(item => uploadQueue.register(async () => {
          const responseKey = getFrameUploadResponseKey({
            clipId: item.clipId,
            frameType: item.frameType,
            fileName: item.fileName
          });
          const presignedItem = presignedByKey.get(responseKey);

          await withRetry(
            () => uploadBlobToSignedUrl({
              uploadUrl: presignedItem.uploadUrl,
              blob: item.blob,
              contentType: presignedItem.contentType || item.contentType
            }),
            {attempts: 3, delayMs: 400}
          );

          return presignedItem;
        }).promise)
      );

      const uploadedItems = [];
      const uploadErrors = [];

      uploadResults.forEach((result, index) => {
        const item = uploadCandidates[index];

        if (result.status === 'fulfilled') {
          uploadedItems.push(result.value);
          return;
        }

        uploadErrors.push({
          index,
          clipId: item?.clipId,
          frameType: item?.frameType,
          fileName: item?.fileName,
          message: result.reason?.message || 'Upload failed'
        });
      });

      const uploadedByKey = new Map(
        uploadedItems.map(item => [
          getFrameUploadResponseKey({
            clipId: item.clipId,
            frameType: item.frameType,
            fileName: item.fileName
          }),
          item
        ])
      );

      for (const target of frameTargets) {
        const fileName = getFrameFileName({frameType: target.frameType});
        const uploadedItem = uploadedByKey.get(
          getFrameUploadResponseKey({
            clipId: target.clipId,
            frameType: target.frameType,
            fileName
          })
        );

        if (!uploadedItem?.s3_path) {
          continue;
        }

        target.frame.s3_path = uploadedItem.s3_path;
      }

      await persistEditingSession();

      if (presignedErrors.length || uploadErrors.length) {
        const error = new Error(VIDEO_EDITOR_TITLE_FRAME_EXPORT_ERROR);
        error.details = {
          presignErrorCount: presignedErrors.length,
          uploadErrorCount: uploadErrors.length,
          uploadedCount: uploadedItems.length,
          // Keep payload small while preserving a concrete failure signal for debugging.
          uploadErrors: [...presignedErrors, ...uploadErrors].slice(0, 3)
        };
        throw error;
      }

      const incompleteTargets = frameTargets.filter(target => !target.frame?.s3_path);
      if (incompleteTargets.length) {
        const error = new Error(VIDEO_EDITOR_TITLE_FRAME_EXPORT_ERROR);
        error.details = {
          incompleteCount: incompleteTargets.length
        };
        throw error;
      }
    } catch (error) {
      if (error?.message === VIDEO_EDITOR_TITLE_FRAME_EXPORT_ERROR) {
        throw error;
      }

      const exportError = new Error(VIDEO_EDITOR_TITLE_FRAME_EXPORT_ERROR);
      exportError.cause = error;
      throw exportError;
    }
  }

  async function submit({mode = VIDEO_EDITOR_CLIP_MODE.clip, contextType, contextId, durationSeconds} = {}) {
    if (!clips.value?.length) {
      throw new Error('Cannot submit: no clips defined');
    }

    const tooShortClips = clips.value.filter(clip => {
      const length = getClipLengthSeconds(clip);
      return length === null || length < VIDEO_EDITOR_MIN_CLIP_DURATION_SECONDS;
    });

    if (tooShortClips.length) {
      const error = new Error(VIDEO_EDITOR_CLIP_TOO_SHORT_ERROR);
      error.details = {
        clipNames: tooShortClips.map(clip => clip.name)
      };
      throw error;
    }

    // Prefer the live player duration passed in by the caller - it's the same value the
    // timeline uses to clamp marker positions, so a full-span clip can never exceed it.
    // The file record's duration_seconds is a fallback for when no player is available.
    const fileDurationSeconds = Number(durationSeconds) || getFileDurationSeconds(file.value);
    if (fileDurationSeconds > 0) {
      const outOfBoundsClips = clips.value.filter(clip => {
        const range = getClipRange(clip);
        return !range || range.start < 0 || range.end > fileDurationSeconds;
      });

      if (outOfBoundsClips.length) {
        const error = new Error(VIDEO_EDITOR_CLIP_OUT_OF_BOUNDS_ERROR);
        error.details = {
          clipNames: outOfBoundsClips.map(clip => clip.name)
        };
        throw error;
      }
    }

    if (_submitPromise) {
      return _submitPromise;
    }

    const coreLoadingEvent = useEventBus('core-loading-set');

    isSubmittingExport.value = true;

    _submitPromise = (async () => {
      stopAutoPersist();

      coreLoadingEvent.emit({
        isLoading: true,
        icon: 'i-ri-file-edit-line',
        message: 'Preparing your edits...'
      });

      await persistEditingSession();
      const service = useVideoEditorApi();

      await persistEditingSession();
      await exportPendingTitleFrames({service});

      coreLoadingEvent.emit({
        isLoading: true,
        icon: 'i-ri-checkbox-circle-line',
        message: 'Wrapping up...'
      });
      await persistEditingSession();
      const clipsPayload = buildPersistableClipsPayload(clips.value);
      await service.createVideoClipJob({
        fileId: file.value.id,
        mode,
        clips: clipsPayload,
        contextType,
        contextId
      });
    })();

    try {
      return await _submitPromise;
    } finally {
      _submitPromise = null;
      isSubmittingExport.value = false;

      coreLoadingEvent.emit({
        isLoading: false
      });
    }
  }

  return {
    isSubmittingExport,
    exportPendingTitleFrames,
    submit
  };
}
